import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Stack, Alert } from '@mui/material';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { useMutation } from 'react-query';
import axios from 'api/request';
import { IpamLookups } from 'api/ipam-lookups';
import Empty from 'components/empty';
import ipamAdminStyle from 'pages/ipam-admin/style';
import IpamAdminHeader from 'pages/ipam-admin/header';
import { ipamAdminSectionState, ipamAdminSectionOptionsState, ipamAdminViewModeState } from 'pages/ipam-admin/store';

const RegionsPage = React.lazy(() => import('pages/ipam-admin/regions'));
const SuperSupernetPage = React.lazy(() => import('pages/ipam-admin/super-supernet'));
const SupernetPage = React.lazy(() => import('pages/ipam-admin/supernet'));
const HubPage = React.lazy(() => import('pages/ipam-admin/hub'));
const AllocationPage = React.lazy(() => import('pages/ipam-admin/allocation'));

const FILTER_TYPE_MAP = {
    'Super-Supernet': { value: 'super-supernet', label: 'Super - Supernet' },
    Region: { value: 'regions', label: 'Regions' },
    Supernet: { value: 'supernet', label: 'Supernet' },
    Hub: { value: 'hub', label: 'Hub' },
    Allocation: { value: 'allocation', label: 'Allocation' },
};

const IpamAdminPage = () => {
    const [section, setSection] = useRecoilState(ipamAdminSectionState);
    const setSectionOptions = useSetRecoilState(ipamAdminSectionOptionsState);
    const viewMode = useRecoilValue(ipamAdminViewModeState);

    const filterTypesApi = useMutation(() => axios.get(IpamLookups.filterTypes));

    React.useEffect(() => {
        filterTypesApi.mutate();
    }, []);

    React.useEffect(() => {
        if (filterTypesApi.data) {
            const types = filterTypesApi.data?.data || [];
            const options = Object.keys(FILTER_TYPE_MAP)
                .filter((key) => types.includes(key))
                .map((key) => FILTER_TYPE_MAP[key]);
            setSectionOptions(options);
            if (options.length && !section) setSection(options[0].value);
        }
    }, [filterTypesApi.data]);

    return (
        <>
            <Helmet>
                <title>IPAM Admin Portal - DevX Infra</title>
            </Helmet>
            <Box sx={ipamAdminStyle.pageWrapper} className="contentContainer" data-testid="ipam-admin-page">
                {/* Page Header */}
                <IpamAdminHeader />

                {/* Section Content */}
                {filterTypesApi.isError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        Failed to load IPAM sections. Please refresh the page and try again.
                    </Alert>
                )}
                <Suspense
                    fallback={
                        <Stack sx={ipamAdminStyle.loadingStack}>
                            <Empty color="#000000" isLoading />
                        </Stack>
                    }
                >
                    {section === 'regions' && <RegionsPage />}
                    {section === 'super-supernet' && <SuperSupernetPage />}
                    {section === 'supernet' && <SupernetPage />}
                    {section === 'hub' && <HubPage />}
                    {section === 'allocation' && <AllocationPage viewMode={viewMode} />}
                </Suspense>
            </Box>
        </>
    );
};

export default IpamAdminPage;
